/**
 * Toast Notification Component
 * Replaces native browser alert() with styled toast
 */

import { useState, useEffect, useCallback, createContext, useContext } from 'react'
import { X, CheckCircle, AlertTriangle, AlertCircle, Info } from 'lucide-react'
import './toast.css'

type ToastVariant = 'success' | 'error' | 'warning' | 'info'

interface ToastItem {
  id: number
  message: string
  variant: ToastVariant
}

interface ToastContextValue {
  toast: (message: string, variant?: ToastVariant) => void
  toastSuccess: (message: string) => void
  toastError: (message: string) => void
  toastWarning: (message: string) => void
  toastInfo: (message: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

let nextId = 0

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const addToast = useCallback((message: string, variant: ToastVariant = 'info') => {
    const id = nextId++
    setToasts((prev) => [...prev, { id, message, variant }])
    // Auto-dismiss after 4 seconds
    setTimeout(() => removeToast(id), 4000)
  }, [removeToast])

  const value: ToastContextValue = {
    toast: addToast,
    toastSuccess: useCallback((msg: string) => addToast(msg, 'success'), [addToast]),
    toastError: useCallback((msg: string) => addToast(msg, 'error'), [addToast]),
    toastWarning: useCallback((msg: string) => addToast(msg, 'warning'), [addToast]),
    toastInfo: useCallback((msg: string) => addToast(msg, 'info'), [addToast])
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-container" aria-live="polite">
        {toasts.map((t) => (
          <ToastNotification key={t.id} toast={t} onDismiss={() => removeToast(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

function ToastNotification({ toast, onDismiss }: { toast: ToastItem; onDismiss: () => void }) {
  const [exiting, setExiting] = useState(false)

  useEffect(() => {
    // Start exit animation 300ms before removal
    const timer = setTimeout(() => setExiting(true), 3700)
    return () => clearTimeout(timer)
  }, [])

  const icons = {
    success: CheckCircle,
    error: AlertCircle,
    warning: AlertTriangle,
    info: Info
  }

  const Icon = icons[toast.variant]

  return (
    <div className={`toast-item toast-${toast.variant} ${exiting ? 'toast-exit' : ''}`}>
      <Icon size={18} className="toast-icon" />
      <span className="toast-message">{toast.message}</span>
      <button className="toast-dismiss" onClick={onDismiss} aria-label="Dismiss">
        <X size={14} />
      </button>
    </div>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used within <ToastProvider>')
  }
  return ctx
}
